import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories, siteInfo } from "@/mocks/articles";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "深度复盘", to: "/insights" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement | null>(null);

  const today = useMemo(() => {
    const d = new Date();
    const week = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][d.getDay()];
    return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 ${week}`;
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/insights?q=${encodeURIComponent(q)}` : "/insights");
    setSearchOpen(false);
    setQuery("");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap px-3 py-2 text-sm transition-colors ${
      isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-background-50/95 backdrop-blur transition-colors ${
        scrolled ? "border-background-300" : "border-background-200"
      }`}
    >
      <div className="hidden border-b border-background-200 md:block">
        <div className="mx-auto flex max-w-editorial items-center justify-between px-6 py-2 text-xs text-foreground-500">
          <span className="font-label">{today}</span>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5 text-secondary-700">
              <i className="ri-pulse-line" aria-hidden="true"></i>
              今日已更新 3 篇复盘
            </span>
            <a href={`mailto:${siteInfo.tipEmail}`} className="transition-colors hover:text-primary-600">
              爆料线索：{siteInfo.tipEmail}
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-editorial items-center justify-between gap-4 px-4 py-3 md:px-6 md:py-4">
        <div className="flex items-center">
          <Link to="/" className="flex items-center gap-2.5" aria-label="看料吃瓜 首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-background-50">
              料
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
                看料吃瓜
              </span>
              <span className="mt-0.5 hidden text-[11px] tracking-[0.25em] text-foreground-500 sm:block">
                深挖 · 复盘
              </span>
            </span>
          </Link>
        </div>

        <nav className="hidden items-center lg:flex" aria-label="主导航">
          {NAV_ITEMS.slice(0, 2).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}

          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              aria-expanded={catOpen}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
            >
              主题分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} aria-hidden="true"></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-[440px] rounded-lg border border-background-200 bg-background-50 p-3">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/insights?category=${cat.slug}`}
                      className="group flex items-start gap-2 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span className="mt-0.5 flex h-6 w-6 items-center justify-center text-secondary-600">
                        <i className={`${cat.icon} text-base`} aria-hidden="true"></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {cat.name}
                        </span>
                        <span className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                          {cat.desc}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {NAV_ITEMS.slice(2).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="搜索"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className={`${searchOpen ? "ri-close-line" : "ri-search-line"} text-base`} aria-hidden="true"></i>
          </button>
          <Link
            to="/insights"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            开始复盘
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-100">
          <form onSubmit={submitSearch} className="mx-auto flex max-w-editorial items-center gap-2 px-4 py-3 md:px-6">
            <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索事件、人物或关键词，例如「短剧」「舆情」"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="站内搜索"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-foreground-950 px-3.5 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="mx-auto max-w-editorial px-4 py-3" aria-label="移动端导航">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `block border-b border-background-100 px-1 py-3 text-sm ${
                    isActive ? "text-primary-600" : "text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <p className="px-1 pb-2 pt-4 text-xs tracking-widest text-foreground-400">主题分类</p>
            <div className="grid grid-cols-2 gap-2 pb-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/insights?category=${cat.slug}`}
                  className="flex items-center gap-2 rounded-md border border-background-200 px-2.5 py-2 text-sm text-foreground-800"
                >
                  <i className={`${cat.icon} text-secondary-600`} aria-hidden="true"></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}